import { useConnectionState, useLocalParticipant } from '@livekit/components-react'
import { ConnectionState } from 'livekit-client'
import { useEffect, useRef, useState } from 'react'
import type { SaverLevel } from './dataSaver'
import { report } from './diagnostics'
import type { ShareHelpTopic } from './help/shareHelp'
import { t } from './i18n'
import { MutedSpeakingHint } from './MutedSpeakingHint'
import { SharePrompt } from './SharePrompt'
import { type ShareFailure, shareFailureKey } from './screenShare'
import { UnmutePrompt } from './UnmutePrompt'
import type { BackgroundStatus } from './useBackgroundEffect'
import type { ShareAnswer, ShareRequest, UnmuteRequest } from './useClassroomSignals'
import type { DeviceNotice } from './useDevices'
import type { NoiseFilterStatus } from './useNoiseFilter'
import type { PipApi } from './usePictureInPicture'

/**
 * Every transient message in the room, in ONE floating layer.
 *
 * These used to be flex items: the blur notice was a full-width row inside the
 * control bar, and the reconnect banner and unmute prompt were rows in the shell
 * column. So each time one appeared it grew its parent and shoved the video —
 * mid-lesson, while a child was watching. A status message must never move the
 * thing it is a status about.
 *
 * This layer is absolutely positioned over the bottom of the lesson area, so
 * anything in it costs zero layout. `pointer-events: none` on the container keeps
 * taps falling through to the video and controls; only the interactive prompt
 * re-enables them for itself.
 *
 * The container is column-reverse, so the FIRST child in the DOM renders lowest.
 * Order below is therefore: interactive prompt nearest the thumb, then the blur
 * notice, then the reconnect banner highest — most urgent on top.
 */
export function RoomToasts({
  backgroundStatus,
  audioBlocked,
  onUnlockAudio,
  unmuteRequest,
  onDismissUnmute,
  saverLevel,
  saverManual,
  saverNoteDismissed,
  onSaverOff,
  onSaverResume,
  onSaverDismiss,
  ncStatus,
  shareRequest,
  onAnswerShare,
  onDismissShare,
  onShareAcceptStart,
  recoveringMedia,
  shareFailure,
  onDismissShareFailure,
  onShareHelp,
  shareSound,
  shareStoppedAt,
  deviceNotice,
  micBanner,
  isHost,
  onFixMic,
  onDismissMicBanner,
  onUnmuteMic,
  pip,
  onFloatNow,
  credential,
}: {
  backgroundStatus: BackgroundStatus
  /** The browser is refusing to play remote audio — the lesson is silent. */
  audioBlocked?: boolean
  /** Must be reached from a real tap: the gesture IS the fix. */
  onUnlockAudio?: () => void
  /** A device vanished and we are re-acquiring it — reassurance, not an error. */
  recoveringMedia?: boolean
  /** The teacher's own share attempt failed and they deserve to know why. */
  shareFailure?: ShareFailure | null
  onDismissShareFailure?: () => void
  onShareHelp: (topic: ShareHelpTopic) => void
  /**
   * Host-only, and only while THIS person is presenting: whether their share is
   * carrying sound. `undefined` when nobody local is sharing, or for anyone who
   * is not the teacher.
   */
  shareSound?: 'on' | 'silent'
  /**
   * When a teacher last stopped MY screen share (0 = never). A timestamp rather
   * than a boolean so a second stop later in the lesson re-opens the note.
   */
  shareStoppedAt?: number
  unmuteRequest: UnmuteRequest | null
  onDismissUnmute: () => void
  saverLevel: SaverLevel
  saverManual: boolean
  /** Dismissal lives in useDataSaver (single source), so the lit control can
   *  re-open this note — the affordance after a child hides it. */
  saverNoteDismissed: boolean
  onSaverOff: () => void
  /** AUTO only: back to L0 and hold the ladder off — the user outranks it. */
  onSaverResume: () => void
  onSaverDismiss: () => void
  /** Drives the transient NC explainer — hosts and students alike (). */
  ncStatus?: NoiseFilterStatus
  /** Student-side ask-to-share prompt (null unless a teacher asked). */
  shareRequest: ShareRequest | null
  onAnswerShare: (request: ShareRequest, answer: ShareAnswer) => void
  onDismissShare: () => void
  onShareAcceptStart: () => void
  /** Device changes are announced here, never by growing the control bar. */
  deviceNotice?: DeviceNotice | null
  micBanner?: boolean
  isHost: boolean
  onFixMic: () => void
  onDismissMicBanner: () => void
  /** The room's existing unmute path, reused by the muted-speaking hint. */
  onUnmuteMic?: () => void
  /** Host-only: pip.canEnter used to show the one-time auto-PiP tip. */
  pip?: PipApi
  onFloatNow?: () => void
  credential: { code?: string | null; reconnectToken?: string | null }
}) {
  const state = useConnectionState()
  const { isCameraEnabled } = useLocalParticipant()
  const recovering =
    state === ConnectionState.Reconnecting || state === ConnectionState.SignalReconnecting
  const blurNote = backgroundStatus === 'loading' || backgroundStatus === 'error'

  const saverNote = saverLevel > 0 && (saverManual || !saverNoteDismissed)

  // The pill says WHETHER noise cancellation runs; this toast says WHAT that
  // means — ONCE per session, the first time it becomes true (or on failure,
  // which is actionable every time). After that the pill state carries it:
  // the filter re-verifies on every reconnect/mic republish (on→loading→on),
  // and re-toasting each time would nag mid-lesson.
  // In a ref so an inline `onDismissShareFailure={() => …}` from the parent
  // cannot re-arm the auto-hide timer on every render and strand the toast.
  const onDismissShareFailureRef = useRef(onDismissShareFailure)
  onDismissShareFailureRef.current = onDismissShareFailure
  const credentialRef = useRef(credential)
  credentialRef.current = credential

  const [ncNote, setNcNote] = useState<'on' | 'failed' | null>(null)
  const [shownDeviceNotice, setShownDeviceNotice] = useState<DeviceNotice | null>(null)
  const [pipTip, setPipTip] = useState(false)
  const [pipTipDismissed, setPipTipDismissed] = useState(false)
  const prevNcRef = useRef<NoiseFilterStatus | undefined>(undefined)
  const ncOnShownRef = useRef(false)
  useEffect(() => {
    const previous = prevNcRef.current
    prevNcRef.current = ncStatus
    if (ncStatus === previous || ncStatus === undefined) return
    if (ncStatus === 'on') {
      if (!ncOnShownRef.current) {
        ncOnShownRef.current = true
        setNcNote('on')
      } else {
        // A retry that lands while the failure toast is still up: the pill
        // says On, the toast must not keep saying it could not start.
        setNcNote((note) => (note === 'failed' ? null : note))
      }
      return
    }
    if (ncStatus === 'failed') setNcNote('failed')
    // 'off'/'unsupported' end any visible story. 'loading' deliberately does
    // NOT: a warm re-engage passes through it briefly, and clearing here would
    // cut the once-per-session explainer short with no way to re-show it.
    else if (ncStatus !== 'loading') setNcNote(null)
  }, [ncStatus])

  // The hide timer belongs to the NOTE, not to the status transition. Tying
  // it to the transition let any later flap (a reconnect re-engage) run the
  // effect cleanup, cancel the pending hide, and strand the toast on screen
  // forever — the reported stuck-toast bug.
  useEffect(() => {
    if (!ncNote) return
    const timer = window.setTimeout(() => setNcNote(null), ncNote === 'on' ? 7_000 : 9_000)
    return () => window.clearTimeout(timer)
  }, [ncNote])

  useEffect(() => {
    if (!deviceNotice) return
    setShownDeviceNotice(deviceNotice)
    const timer = window.setTimeout(() => setShownDeviceNotice(null), 4_000)
    return () => window.clearTimeout(timer)
  }, [deviceNotice])

  // Auto-hide, like the noise-cancellation note: a share failure is worth
  // reading once, and an error card that outlives the problem becomes furniture
  // the teacher stops seeing.
  useEffect(() => {
    if (!shareFailure) return
    const timer = window.setTimeout(() => onDismissShareFailureRef.current?.(), 9_000)
    return () => window.clearTimeout(timer)
  }, [shareFailure])

  // The two halves of the share-sound story, and they are deliberately not the
  // same shape.
  //
  // "No sound" PERSISTS while it is true, because it is a live fact about what
  // the class is receiving and it stays fixable for as long as the share runs —
  // an auto-hiding toast would be gone by the time a child says they cannot hear
  // anything. It is dismissible, and the dismissal is dropped when the share ends
  // so the next share can say it again.
  //
  // "Sound is on" is a one-time nudge and auto-hides, like the noise-cancellation
  // explainer. It says the useful thing once — the teacher's own speakers are now
  // an open path back into their microphone — and then never again, because
  // repeating it every share is nagging.
  const [soundNoteDismissed, setSoundNoteDismissed] = useState(false)
  const [soundOnNote, setSoundOnNote] = useState(false)
  const soundOnShownRef = useRef(false)
  useEffect(() => {
    // Anything other than 'on' retires the nudge IMMEDIATELY rather than waiting
    // for its timer. The sequence this exists for is a share whose audio dies
    // partway through (Chrome's own sharing bar can switch the shared tab and end
    // the audio track while the video carries on): 'on' → 'silent' would
    // otherwise leave "the class can hear the shared sound" on screen directly
    // beneath "this share has no sound", and the container stacks later children
    // higher, so the stale reassurance would sit ON TOP of the live problem.
    if (shareSound !== 'on') setSoundOnNote(false)
    if (!shareSound) {
      setSoundNoteDismissed(false)
      return
    }
    if (shareSound === 'on' && !soundOnShownRef.current) {
      soundOnShownRef.current = true
      setSoundOnNote(true)
    }
  }, [shareSound])

  // One-time host nudge: when PiP becomes possible, explain that switching
  // windows/tabs is what it is for. A host who has already dismissed it never
  // sees it again in this session.
  const pipCanEnter = pip?.canEnter ?? false
  const pipActive = pip?.active ?? false
  const pipSeen = typeof window !== 'undefined' && localStorage.getItem('meet.pipTipSeen') === '1'
  useEffect(() => {
    if (!isHost || pipTipDismissed || pipSeen) return
    if (pipCanEnter && !pipActive) {
      setPipTip(true)
      report(credentialRef.current, {
        stage: 'permissions',
        outcome: 'ok',
        permission: 'pip=tip-shown',
      })
    }
  }, [isHost, pipCanEnter, pipActive, pipTipDismissed, pipSeen])

  const dismissPipTip = () => {
    setPipTip(false)
    setPipTipDismissed(true)
    try {
      localStorage.setItem('meet.pipTipSeen', '1')
    } catch {
      /* private mode */
    }
  }

  // On the note, not the transition — the same stuck-toast lesson as ncNote.
  useEffect(() => {
    if (!soundOnNote) return
    const timer = window.setTimeout(() => setSoundOnNote(false), 9_000)
    return () => window.clearTimeout(timer)
  }, [soundOnNote])

  const silentShareNote = shareSound === 'silent' && !soundNoteDismissed

  // A teacher stopping a child's share used to be completely silent from the
  // child's side: their picture vanished from the stage and nothing said why.
  // Keyed on the timestamp so a second stop later in the lesson speaks again.
  const [stoppedNote, setStoppedNote] = useState(0)
  useEffect(() => {
    // Reset to 0 means "sharing again" — the signal clears itself when a new
    // local share publishes, and the note has to go with it.
    if (!shareStoppedAt) {
      setStoppedNote(0)
      return
    }
    setStoppedNote(shareStoppedAt)
    const timer = window.setTimeout(() => setStoppedNote(0), 9_000)
    return () => window.clearTimeout(timer)
  }, [shareStoppedAt])

  if (
    !recovering &&
    !blurNote &&
    !unmuteRequest &&
    !saverNote &&
    !shownDeviceNotice &&
    !ncNote &&
    !shareRequest &&
    !shareFailure &&
    !silentShareNote &&
    !soundOnNote &&
    !stoppedNote &&
    !micBanner &&
    !pipTip
  )
    return null

  return (
    <div className="room-toasts">
      {/* Deliberately the calmest possible wording. From the child's side a
          headset coming loose is not a failure, and the recovery usually lands in
          a couple of seconds — saying "problem" would frighten them into
          reloading, which is exactly what burns join codes. */}
      {recoveringMedia ? (
        <div className="toast" role="status" aria-live="polite">
          <span>{t('recovery.reconnectingMic')}</span>
        </div>
      ) : null}
      {/* Why the share button did nothing. Interactive (it has a dismiss), and
          worded as a next action rather than an error code — on Windows the
          commonest one by far is InvalidStateError, which simply means "click it
          again with this window in front". */}
      {shareFailure ? (
        <div className="toast toast--saver" role="status" aria-live="polite">
          <span>{t(shareFailureKey(shareFailure))}</span>
          {shareFailure !== 'cancelled' && shareFailure !== 'unsupported' ? (
            <button
              type="button"
              className="pill-button pill-button--accent"
              onClick={() => onShareHelp(shareFailure)}
            >
              {t('share.help.show')}
            </button>
          ) : null}
          <button
            type="button"
            className="toast__btn toast__btn--quiet"
            onClick={onDismissShareFailure}
          >
            {t('share.close')}
          </button>
        </div>
      ) : null}

      {/* The browser is refusing to play the lesson's audio, and nothing else
          in the room can say so — every tile looks healthy while the child
          hears silence. Rendered ABOVE the other prompts in the DOM (this
          container is column-reverse, so it lands nearest the thumb) because
          there is no message in this app more urgent than "you cannot hear
          the class". The whole point is the tap: the gesture is what the
          autoplay policy is waiting for. */}
      {audioBlocked ? (
        <div className="unmute-prompt" role="alertdialog" aria-labelledby="audio-blocked-title">
          <span className="unmute-prompt__icon" aria-hidden="true">
            🔈
          </span>
          <p className="unmute-prompt__text" id="audio-blocked-title">
            {t('audio.blocked')}
          </p>
          <div className="unmute-prompt__actions">
            <button
              type="button"
              className="pill-button pill-button--accent"
              onClick={onUnlockAudio}
            >
              {t('audio.unlock')}
            </button>
          </div>
        </div>
      ) : null}

      {micBanner ? (
        <div className="unmute-prompt" role="status" aria-live="polite">
          <span className="unmute-prompt__icon" aria-hidden="true">
            🎙️
          </span>
          <p className="unmute-prompt__text">
            {t(isHost ? 'room.micBanner.host' : 'room.micBanner.student')}
          </p>
          <div className="unmute-prompt__actions">
            <button type="button" className="pill-button pill-button--accent" onClick={onFixMic}>
              {t('room.fix')}
            </button>
            <button
              type="button"
              className="pill-button pill-button--quiet"
              onClick={onDismissMicBanner}
            >
              {t('room.dismiss')}
            </button>
          </div>
        </div>
      ) : null}

      {/* Local, self-measured: "You're muted" while this device still detects
          its own speech. Informational, so it sits under the interactive
          prompts in the stack (this container is column-reverse). */}
      {onUnmuteMic ? <MutedSpeakingHint onUnmute={onUnmuteMic} /> : null}

      {/* Interactive prompts sit lowest — nearest the thumb. */}
      {shareRequest ? (
        <SharePrompt
          key={shareRequest.id}
          request={shareRequest}
          onAnswer={onAnswerShare}
          onDismiss={onDismissShare}
          onAcceptStart={onShareAcceptStart}
        />
      ) : null}

      {unmuteRequest ? (
        <UnmutePrompt
          // Keyed by request id so a second ask re-opens a prompt the student
          // dismissed, instead of being swallowed.
          key={unmuteRequest.id}
          request={unmuteRequest}
          onDismiss={onDismissUnmute}
        />
      ) : null}

      {pipTip ? (
        <div className="unmute-prompt" role="status" aria-live="polite">
          <span className="unmute-prompt__icon" aria-hidden="true">
            💡
          </span>
          <p className="unmute-prompt__text">
            <strong>{t('pip.tipTitle')}</strong>
            <br />
            {t('pip.tipBody')}
          </p>
          <div className="unmute-prompt__actions">
            <button
              type="button"
              className="pill-button pill-button--accent"
              onClick={() => {
                onFloatNow?.()
                dismissPipTip()
              }}
            >
              {t('pip.tipFloatNow')}
            </button>
            <button
              type="button"
              className="pill-button pill-button--quiet"
              onClick={dismissPipTip}
            >
              {t('pip.tipGotIt')}
            </button>
          </div>
        </div>
      ) : null}

      {/* The honest half of the data saver: a lesson with videos off must say
          WHY, or the missing faces read as the class being broken — and the
          parent blames the teacher. Interactive, so pointer events re-enable. */}
      {saverNote ? (
        <div className="toast toast--saver" role="status" aria-live="polite">
          <span>
            {t(saverManual ? 'saver.onManual' : saverLevel === 1 ? 'saver.autoL1' : 'saver.autoL2')}
            {isCameraEnabled ? ` ${t('saver.cameraStillOn')}` : null}
          </span>
          {saverManual ? (
            <button type="button" className="toast__btn" onClick={onSaverOff}>
              {t('saver.turnOff')}
            </button>
          ) : (
            <>
              {/* The escape hatch the first cohort refreshed the page to get:
                  the machine's judgment is advisory, the human's is final. */}
              <button type="button" className="toast__btn" onClick={onSaverResume}>
                {t('saver.resume')}
              </button>
              <button
                type="button"
                className="toast__btn toast__btn--quiet"
                onClick={onSaverDismiss}
              >
                {t('saver.dismiss')}
              </button>
            </>
          )}
        </div>
      ) : null}

      {shownDeviceNotice ? (
        <div className="toast toast--saver" role="status" aria-live="polite">
          <span>
            {shownDeviceNotice.type === 'nowUsing'
              ? t('devices.nowUsing').replace(
                  '{device}',
                  shownDeviceNotice.label || t('devices.systemDefault'),
                )
              : shownDeviceNotice.type === 'deviceGone'
                ? t('devices.deviceGone')
                : t('devices.switchFailed')}
          </span>
        </div>
      ) : null}

      {/* Not toast--error, deliberately. Nothing failed: Chrome hid a checkbox
          and the teacher did not find it, which is the browser's doing and not
          theirs. An error card here would teach them that sharing is fragile. */}
      {silentShareNote ? (
        <div className="toast toast--saver toast--multiline" role="status" aria-live="polite">
          <span>{t('share.noSound')}</span>
          <button
            type="button"
            className="pill-button pill-button--accent"
            onClick={() => onShareHelp('noSound')}
          >
            {t('share.help.show')}
          </button>
          <button
            type="button"
            className="toast__btn toast__btn--quiet"
            onClick={() => setSoundNoteDismissed(true)}
          >
            {t('share.close')}
          </button>
        </div>
      ) : null}

      {soundOnNote ? (
        <div className="toast toast--multiline" role="status" aria-live="polite">
          <span>{t('share.soundOn')}</span>
        </div>
      ) : null}

      {/* Not an error: a teacher ending a share is a normal part of running a
          lesson, and a child who was showing their work should not be made to
          feel they did something wrong. */}
      {stoppedNote ? (
        <div className="toast toast--multiline" role="status" aria-live="polite">
          <span>{t('share.stoppedByTeacher')}</span>
        </div>
      ) : null}

      {ncNote ? (
        <div
          className={`toast toast--multiline ${ncNote === 'failed' ? 'toast--error' : ''}`}
          role="status"
          aria-live="polite"
        >
          <span>
            {t(ncNote === 'on' ? (isHost ? 'nc.toastOn' : 'nc.toastOnStudent') : 'nc.failed')}
          </span>
        </div>
      ) : null}

      {blurNote ? (
        <div
          className={`toast ${backgroundStatus === 'error' ? 'toast--error' : ''}`}
          role="status"
          aria-live="polite"
        >
          {backgroundStatus === 'error' ? null : (
            <span className="toast__spinner" aria-hidden="true" />
          )}
          <span>{t(backgroundStatus === 'error' ? 'room.blurFailed' : 'room.blurStarting')}</span>
        </div>
      ) : null}
      {recovering ? (
        <div className="toast toast--warn" role="status" aria-live="polite">
          <span className="toast__spinner" aria-hidden="true" />
          <span>{t('room.reconnecting')}</span>
        </div>
      ) : null}
    </div>
  )
}
